import React, { useState } from 'react';
import { GeneratedImage, AppSettings } from '../types';
import { X, Sparkles, Wand2 } from 'lucide-react';
import { editImage } from '../services/geminiService';

interface EditModalProps {
  image: GeneratedImage;
  isOpen: boolean;
  onClose: () => void;
  onSave: (newImage: GeneratedImage) => void;
}

const EditModal: React.FC<EditModalProps> = ({ image, isOpen, onClose, onSave }) => {
  const [prompt, setPrompt] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentPreview, setCurrentPreview] = useState(image.url);

  if (!isOpen) return null;

  const handleEdit = async () => {
    if (!prompt.trim() || !image.base64Data) return;
    
    // Retrieve settings for API Key
    const savedSettings = localStorage.getItem('appSettings');
    if (!savedSettings) {
        alert("请先配置 API 设置。");
        return;
    }
    const settings: AppSettings = JSON.parse(savedSettings);

    setIsProcessing(true);
    try {
      // For editing, we always send the *original* or *currently displayed* image
      // Note: If we wanted to chain edits, we would need to track the latest base64. 
      // For simplicity, we are editing the base version for now, but in a real app, track the current blob.
      const result = await editImage(image.base64Data, 'image/png', prompt, settings);
      
      const newUrl = `data:${result.mimeType};base64,${result.base64}`;
      setCurrentPreview(newUrl);

      // We could auto-save or let user choose. Let's auto-save to history conceptually.
      onSave({
        ...image,
        id: Math.random().toString(), // New ID for the edited version
        url: newUrl,
        base64Data: result.base64
      });

      setPrompt(''); // Clear prompt after success
    } catch (error) {
      alert("编辑失败，请重试。");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-slate-900 rounded-xl border border-slate-700 w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-yellow-500/10 rounded-lg">
                <Wand2 className="w-5 h-5 text-yellow-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">魔法编辑</h3>
              <p className="text-xs text-slate-400">由 Gemini 2.5 Flash Image 驱动</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-full text-slate-400 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
          {/* Image Area */}
          <div className="flex-1 bg-black/50 p-6 flex items-center justify-center relative">
            <img 
              src={currentPreview} 
              alt="Editing preview" 
              className="max-w-full max-h-full object-contain rounded shadow-lg border border-slate-800"
            />
            {isProcessing && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-[2px]">
                <div className="flex flex-col items-center gap-3">
                   <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                   <span className="text-indigo-300 font-medium animate-pulse">正在优化图片...</span>
                </div>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="w-full md:w-80 bg-slate-800 p-6 flex flex-col gap-4 border-l border-slate-700">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">编辑指令</label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="例如：变成日落，添加一顶红帽子，移除背景..."
                className="w-full h-32 bg-slate-900 border border-slate-700 rounded-lg p-3 text-sm text-slate-200 placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none outline-none"
              />
            </div>
            
            <button
              onClick={handleEdit}
              disabled={isProcessing || !prompt.trim()}
              className="w-full py-3 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium shadow-lg shadow-indigo-500/20 flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
            >
              <Sparkles size={18} />
              生成编辑
            </button>

            <div className="mt-auto p-4 bg-slate-900/50 rounded-lg border border-slate-700/50">
               <p className="text-xs text-slate-400 leading-relaxed">
                 <strong className="text-slate-300">提示：</strong> 自然地描述修改内容。Gemini 可以理解“将季节改为冬天”或“添加未来霓虹光效”等复杂指令。
               </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditModal;